/**
 * Every facility or facility group the app names links to its page on Facilities
 * (review #226: "wherever … facilities are listed … can always drill through").
 *
 * In the URL, not router state: a facility names no person (ui/urlState.ts). Pushed,
 * so Back returns to wherever it was clicked.
 */
import { useCallback } from 'react'
import type { MouseEvent, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Column } from './Table'

export type FacilitySubject = { facility: string } | { group: string }

export const facilityHref = (s: FacilitySubject) =>
  'facility' in s
    ? `/facilities?facility=${encodeURIComponent(s.facility)}`
    : `/facilities?fgroup=${encodeURIComponent(s.group)}`

export function useOpenFacility(): (s: FacilitySubject) => void {
  const navigate = useNavigate()
  return useCallback((s: FacilitySubject) => navigate(facilityHref(s)), [navigate])
}

/**
 * A text column whose cells open the facility or group they name. Blank and "Unknown"
 * stay plain: there is no such facility to open.
 */
export function linked<R>(col: Column<R>, as: 'facility' | 'group'): Column<R> {
  return {
    ...col,
    render: (r: R) => {
      const v = col.render(r)
      if (typeof v !== 'string' || !v || v === 'Unknown') return v
      return as === 'facility' ? <FacilityLink facility={v} /> : <FacilityLink group={v} />
    },
  }
}

/**
 * The name as a link. Stops the click there, so inside a clickable row it opens the
 * facility rather than the row's own drill.
 */
export function FacilityLink({
  facility,
  group,
  children,
}: {
  facility?: string
  group?: string
  children?: ReactNode
}) {
  const open = useOpenFacility()
  const subject: FacilitySubject | null =
    facility !== undefined ? { facility } : group !== undefined ? { group } : null
  if (!subject) return <>{children}</>
  const name = 'facility' in subject ? subject.facility : subject.group
  return (
    <a
      className="facility-link"
      href={facilityHref(subject)}
      title={`Open ${name} on Facilities`}
      onClick={(e: MouseEvent) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return
        e.preventDefault()
        e.stopPropagation()
        open(subject)
      }}
    >
      {children ?? name}
    </a>
  )
}
